import React, { Dispatch, SetStateAction, memo, useCallback } from "react";
import { useIsMobile } from "hooks/useIsMobile";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AdminModal } from "components/admin";
import { useToastStore } from "store/toastStore";
import { ReferralCreditConfigRequest } from "apis/adminServices/referralServices";

export type ReferralFormConfig = ReferralCreditConfigRequest;

/**
 * Format a Date as YYYY-MM-DD using local components, avoiding the
 * off-by-one shift caused by `toISOString()` converting to UTC.
 */
const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

interface AddReferralModalProps {
  newConfig: ReferralFormConfig;
  setNewConfig: Dispatch<SetStateAction<ReferralFormConfig>>;
  onAdd: (e: React.FormEvent) => Promise<void> | void;
  onClose: () => void;
  loading?: boolean;
}

const AddReferralModal: React.FC<AddReferralModalProps> = memo(
  ({ newConfig, setNewConfig, onAdd, onClose, loading = false }) => {
    const { showToast } = useToastStore();
    const isMobile = useIsMobile();

    const handleFieldChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { id, value, type } = e.target;
        setNewConfig((prev) => ({
          ...prev,
          [id]:
            type === "number"
              ? parseInt(value, 10) || 0
              : id === "isActive"
              ? value === "true"
              : value,
        }));
      },
      [setNewConfig]
    );

    const handleValidTillChange = useCallback(
      (date: Date | null) => {
        if (!date) {
          setNewConfig((prev) => ({ ...prev, validTill: "" }));
          return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(date);
        selected.setHours(0, 0, 0, 0);
        if (selected.getTime() < today.getTime()) {
          showToast("Cannot select past dates.", "error");
          return;
        }
        setNewConfig((prev) => ({ ...prev, validTill: toLocalDateString(date) }));
      },
      [setNewConfig, showToast]
    );

    // "Valid Till" is a DatePicker, so the native `required` attribute used by
    // the other fields doesn't apply to it. Validate it explicitly on submit.
    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        if (!newConfig.validTill) {
          e.preventDefault();
          showToast("Please select a Valid Till date.", "error");
          return;
        }
        onAdd(e);
      },
      [newConfig.validTill, onAdd, showToast]
    );

    return (
      <AdminModal isOpen={true} onClose={onClose} title="Add New Referral Configuration" size="xl">
        <form onSubmit={handleSubmit}>
          {/* Config Name */}
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-2" htmlFor="configName">
              Configuration Name
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              id="configName"
              value={newConfig.configName}
              onChange={handleFieldChange}
              className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
              required
              disabled={loading}
            />
          </div>

          {/* Status */}
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-2" htmlFor="isActive">
              Status
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              id="isActive"
              value={String(newConfig.isActive)}
              onChange={handleFieldChange}
              className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
              disabled={loading}
            >
              <option value="true">Active</option>
              <option value="false">Disable</option>
            </select>
          </div>

          {/* Community Credits */}
          <div className="mb-4 flex gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2" htmlFor="referrerCreditAmount">
                Community Referrer Credit Amount
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="referrerCreditAmount"
                value={newConfig.referrerCreditAmount === 0 ? "" : newConfig.referrerCreditAmount}
                onChange={handleFieldChange}
                min="0"
                placeholder="0"
                className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                disabled={loading}
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2" htmlFor="refereeCreditAmount">
                Community Referee Credit Amount
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="refereeCreditAmount"
                value={newConfig.refereeCreditAmount === 0 ? "" : newConfig.refereeCreditAmount}
                onChange={handleFieldChange}
                min="0"
                placeholder="0"
                className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                disabled={loading}
              />
            </div>
          </div>

          {/* Non-Community Credits */}
          <div className="mb-4 flex gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2" htmlFor="nonCommunityReferrerCredit">
                Non-Community Referrer Credit
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="nonCommunityReferrerCredit"
                value={newConfig.nonCommunityReferrerCredit === 0 ? "" : newConfig.nonCommunityReferrerCredit}
                onChange={handleFieldChange}
                min="0"
                placeholder="0"
                className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                disabled={loading}
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2" htmlFor="nonCommunityRefereeCredit">
                Non-Community Referee Credit
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="nonCommunityRefereeCredit"
                value={newConfig.nonCommunityRefereeCredit === 0 ? "" : newConfig.nonCommunityRefereeCredit}
                onChange={handleFieldChange}
                min="0"
                placeholder="0"
                className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                disabled={loading}
              />
            </div>
          </div>

          {/* Community Count */}
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-2" htmlFor="communityCount">
              Community Count
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              id="communityCount"
              value={newConfig.communityCount === 0 ? "" : newConfig.communityCount}
              onChange={handleFieldChange}
              min="0"
              placeholder="0"
              className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
              disabled={loading}
            />
          </div>

          {/* Valid Till */}
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-2" htmlFor="validTill">
              Valid Till
              <span className="text-red-500 ml-1">*</span>
            </label>
            <DatePicker
              id="validTill"
              selected={newConfig.validTill ? new Date(newConfig.validTill) : null}
              onChange={handleValidTillChange}
              dateFormat="dd/MM/yyyy"
              className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
              placeholderText="DD/MM/YYYY"
              minDate={new Date()}
              disabled={loading}
              withPortal={isMobile}
              onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs text-gray-600"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#FFD700] text-white text-xs rounded-lg hover:bg-[#E6C200] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </AdminModal>
    );
  }
);

AddReferralModal.displayName = "AddReferralModal";

export default AddReferralModal;
